import { Box, Button, HStack, Text } from "@chakra-ui/react";
import {
	InspectorControls,
	// @ts-ignore
	useBlockEditContext,
} from "@wordpress/block-editor";
import { createSlotFill } from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import React from "react";
import { withChakraUIProvider } from "../../hoc";
import { useSharedState } from "../../hooks/useSharedState";
import { Advanced, General, Style } from "../icons/Icons";

const TABS = ["general", "style", "advanced"];

const { Slot: GeneralInspectorTabSlot, Fill: GeneralInspectorTabFill } =
	createSlotFill("MagazineBlocksGeneralInspectorTab");
const { Slot: StyleInspectorTabSlot, Fill: StyleInspectorTabFill } =
	createSlotFill("MagazineBlocksStyleInspectorTab");
const { Slot: AdvancedInspectorTabSlot, Fill: AdvancedInspectorTabFill } =
	createSlotFill("MagazineBlocksAdvancedInspectorTab");

export const InspectorTabContext = React.createContext<string>("");

const GeneralInspectorControls = (props: { children: React.ReactNode }) => {
	const { isSelected, name } = useBlockEditContext();
	const [activeTab] = useSharedState(
		`MagazineBlocksInspectorTab-${name}`,
		"general"
	);

	if (!isSelected || activeTab !== "general") {
		return null;
	}
	return <GeneralInspectorTabFill>{props.children}</GeneralInspectorTabFill>;
};

const StyleInspectorControls = (props: { children: React.ReactNode }) => {
	const { isSelected, name } = useBlockEditContext();
	const [activeTab] = useSharedState(
		`MagazineBlocksInspectorTab-${name}`,
		"general"
	);

	if (!isSelected || activeTab !== "style") {
		return null;
	}
	return <StyleInspectorTabFill>{props.children}</StyleInspectorTabFill>;
};

const AdvancedInspectorControls = (props: { children: React.ReactNode }) => {
	const { isSelected, name } = useBlockEditContext();
	const [activeTab] = useSharedState(
		`MagazineBlocksInspectorTab-${name}`,
		"general"
	);

	if (!isSelected || activeTab !== "advanced") {
		return null;
	}
	return (
		<AdvancedInspectorTabFill>{props.children}</AdvancedInspectorTabFill>
	);
};

const InspectorTabs = withChakraUIProvider(() => {
	const { name } = useBlockEditContext();
	const [activeTab, setActiveTab] = useSharedState(
		`MagazineBlocksInspectorTab-${name}`,
		`general`
	);

	const currentIndex = TABS.indexOf(activeTab as string);
	const containerRef = React.useRef<HTMLDivElement>(null);

	const updateEditorSidebarDataAttr = (tabIndex: number) => {
		const sidebarPanel =
			-1 === tabIndex
				? document.querySelector("[data-ba-tab]")
				: containerRef.current?.closest(".components-panel");
		if (!sidebarPanel) return;
		if (-1 === tabIndex) {
			sidebarPanel.removeAttribute("data-ba-tab");
			sidebarPanel
				.closest(
					".edit-post-sidebar, .edit-widgets-sidebar, .interface-complementary-area"
				)
				?.classList.remove("magazine-blocks-inspector--open");
			return;
		}
		setTimeout(() => {
			if (!sidebarPanel) return;
			sidebarPanel.setAttribute("data-ba-tab", TABS[tabIndex]);
			sidebarPanel
				.closest(
					".edit-post-sidebar, .edit-widgets-sidebar, .interface-complementary-area"
				)
				?.classList.add("magazine-blocks-inspector--open");
		}, 1);
	};

	React.useEffect(() => {
		updateEditorSidebarDataAttr(currentIndex);
		return () => {
			updateEditorSidebarDataAttr(-1);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Box ref={containerRef}>
			<HStack
				flexWrap="nowrap"
				gap="6px"
				sx={{
					".chakra-button": {
						display: "flex",
						padding: "12px 20px",
						flexDirection: "column",
						position: "relative",
						h: "auto",
						gap: "1",
						svg: {
							h: "6",
							w: "6",
						},
						flex: 1,
						span: {
							fontSize: "sm",
							fontWeight: "medium",
						},
						"&[data-active]:after": {
							content: '""',
							position: "absolute",
							h: "2px",
							left: "0",
							right: "0",
							bottom: "0",
							bgColor: "primary.500",
						},
						"&[data-active]": {
							color: "primary.500",
						},
					},
				}}
				borderBottom="1px"
				borderTop="1px"
				borderColor="gray.200"
				mb="2px"
			>
				<Button
					isActive={"general" === activeTab}
					variant="unstyled"
					onClick={() => {
						setActiveTab("general");
						updateEditorSidebarDataAttr(0);
					}}
				>
					<General />
					<Text as="span">{__("General", "magazine-blocks")}</Text>
				</Button>
				<Button
					isActive={"style" === activeTab}
					variant="unstyled"
					onClick={() => {
						setActiveTab("style");
						updateEditorSidebarDataAttr(1);
					}}
				>
					<Style />
					<Text as="span">{__("Style", "magazine-blocks")}</Text>
				</Button>
				<Button
					isActive={"advanced" === activeTab}
					variant="unstyled"
					onClick={() => {
						setActiveTab("advanced");
						updateEditorSidebarDataAttr(2);
					}}
				>
					<Advanced />
					<Text as="span">{__("Advanced", "magazine-blocks")}</Text>
				</Button>
			</HStack>
			<GeneralInspectorTabSlot />
			<StyleInspectorTabSlot />
			<AdvancedInspectorTabSlot />
		</Box>
	);
});

const InspectorControlTabs = () => (
	<InspectorControls>
		<InspectorTabs />
	</InspectorControls>
);

export default InspectorControlTabs;

export {
	GeneralInspectorControls,
	StyleInspectorControls,
	AdvancedInspectorControls,
};
